import { Button, ErrorLabel } from '@ibsys/kit';
import { PlusIcon, XIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { KmAttrType } from '@/entities/ontology';
import { IconButton } from '@/shared/ui';
import type { KmAttrFormValue } from './attrFormValue';
import { KmAttrScalarInput } from './KmAttrScalarInput';

type Props = {
  attrType: KmAttrType;
  value?: KmAttrFormValue;
  onChange?: (value: KmAttrFormValue) => void;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
};

/**
 * Набор значений атрибута-списка: каждый элемент набирается так же, как одиночное значение
 * того же типа. Ошибка разбора относится к набору целиком — она показывается под ним.
 */
export const KmAttrListInput: FC<Props> = ({ attrType, value, onChange, disabled, error, placeholder }) => {
  const { t } = useTranslation(['kmEntities', 'common']);
  const items = Array.isArray(value) ? (value as KmAttrFormValue[]) : [];

  const applyItemChange = (index: number, itemValue: KmAttrFormValue) => {
    onChange?.(items.map((item, itemIndex) => (itemIndex === index ? itemValue : item)));
  };

  const applyItemRemove = (index: number) => {
    onChange?.(items.filter((_item, itemIndex) => itemIndex !== index));
  };

  const applyItemAdd = () => {
    onChange?.([...items, '']);
  };

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, index) => (
        // Порядок в наборе и есть различие элементов: своего ключа у значения нет
        <div key={`item-${index}`} className="flex items-center gap-2">
          <KmAttrScalarInput
            attrType={attrType}
            value={item}
            onChange={(next) => applyItemChange(index, next)}
            disabled={disabled}
            placeholder={placeholder}
          />
          {!disabled && (
            <IconButton
              size="icon"
              color="destructive"
              icon={<XIcon />}
              label={t('kmEntities:attrListItemRemove')}
              onClick={() => applyItemRemove(index)}
            />
          )}
        </div>
      ))}

      {!disabled && (
        <Button variant="outline" size="sm" icon={<PlusIcon />} onClick={applyItemAdd} className="self-start">
          {t('kmEntities:attrListItemAdd')}
        </Button>
      )}

      {!!error && <ErrorLabel>{error}</ErrorLabel>}
    </div>
  );
};
